import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { adminApi, formatApiError } from "@/lib/auth";
import { BLOG_CATEGORIES, BLOG_PATH } from "@/lib/blogConfig";
import {
  Save,
  Upload,
  ArrowLeft,
  Loader2,
  Eye,
  EyeOff,
  Trash2,
  Image as ImageIcon,
} from "lucide-react";

export default function AdminEditor({ mode = "new" }) {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = mode === "edit";

  // New (upload) state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFileName, setPdfFileName] = useState("");
  const [uploading, setUploading] = useState(false);

  // Common metadata
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(BLOG_CATEGORIES[0]);
  const [excerpt, setExcerpt] = useState("");
  const [tags, setTags] = useState("");
  const [published, setPublished] = useState(false);
  const [featuredImage, setFeaturedImage] = useState("");

  // Edit state
  const [contentHtml, setContentHtml] = useState("");
  const [slug, setSlug] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get(`/admin/posts/${id}`);
        if (cancelled) return;
        setTitle(data.title || "");
        setCategory(data.category || BLOG_CATEGORIES[0]);
        setExcerpt(data.excerpt || "");
        setTags((data.tags || []).join(", "));
        setPublished(!!data.published);
        setFeaturedImage(data.featured_image || "");
        setContentHtml(data.content_html || "");
        setSlug(data.slug || "");
      } catch (e) {
        setError(formatApiError(e?.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!pdfFile) {
      setError("Please choose a PDF file first.");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", pdfFile);
      form.append("title", title.trim());
      form.append("category", category);
      form.append("excerpt", excerpt.trim());
      form.append("tags", tags);
      form.append("published", String(published));
      const { data } = await adminApi.post("/admin/posts/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // After upload, jump to the edit page so user can review & clean
      nav(`/admin/edit/${data.id}`, { replace: true });
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setError("");
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        category,
        excerpt: excerpt.trim(),
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        featured_image: featuredImage || null,
        content_html: contentHtml,
        published,
      };
      const { data } = await adminApi.put(`/admin/posts/${id}`, payload);
      setSlug(data.slug);
      setPublished(!!data.published);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await adminApi.delete(`/admin/posts/${id}`);
      nav("/admin", { replace: true });
    } catch (e) {
      alert(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <main data-testid={isEdit ? "admin-edit-page" : "admin-new-page"} className="bg-bone min-h-screen">
      <header className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 font-display text-lg tracking-tight"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </Link>
          <div className="flex items-center gap-3">
            {isEdit && (
              <>
                <button
                  onClick={() => setPublished((v) => !v)}
                  data-testid="editor-toggle-publish"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                    published
                      ? "bg-emerald-500 text-white"
                      : "bg-white/10 text-white border border-white/20"
                  }`}
                >
                  {published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {published ? "Published" : "Draft"}
                </button>
                <button
                  onClick={handleDelete}
                  data-testid="editor-delete"
                  className="inline-flex items-center gap-2 text-red-300 hover:text-red-200 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </>
            )}
            {isEdit && (
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="editor-save"
                className="inline-flex items-center gap-2 bg-brass text-ink px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Saving…" : "Save"}
              </button>
            )}
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 mb-6 text-sm">
            {error}
          </div>
        )}

        {!isEdit ? (
          <form onSubmit={handleUpload} data-testid="upload-form" className="space-y-6">
            <p className="overline text-ink">New post</p>
            <h1 className="font-display text-ink text-3xl sm:text-4xl tracking-tight">
              Upload a PDF blog
            </h1>
            <p className="text-text2/80 max-w-2xl">
              Drop in your PDF — I'll extract the text + embedded images and turn it into a
              fully SEO-indexable HTML post. You'll review the result before publishing.
            </p>

            <Field label="PDF file" required>
              <label className="flex items-center justify-center w-full border-2 border-dashed border-border2 hover:border-ink rounded-xl px-6 py-10 cursor-pointer bg-white">
                <input
                  type="file"
                  accept="application/pdf"
                  data-testid="upload-pdf-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setPdfFile(f);
                      setPdfFileName(f.name);
                      if (!title) {
                        const t = f.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ");
                        setTitle(t);
                      }
                    }
                  }}
                  className="hidden"
                />
                <span className="text-center">
                  <Upload className="w-7 h-7 text-brass mx-auto mb-2" />
                  <span className="block font-medium text-ink">
                    {pdfFileName || "Click to choose PDF (max 25MB)"}
                  </span>
                  <span className="block text-muted2 text-xs mt-1">
                    Digital PDFs only — scanned images won't extract.
                  </span>
                </span>
              </label>
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Title (auto-filled from filename)" required>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
              </Field>
              <Field label="Category" required>
                <Select value={category} onChange={(e) => setCategory(e.target.value)}>
                  {BLOG_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Field label="Short excerpt (optional — auto-generated if blank)">
              <Textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={3} />
            </Field>

            <Field label="Tags (comma-separated, optional)">
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="destin, market update, VA loan"
              />
            </Field>

            <label className="inline-flex items-center gap-3 text-sm text-text2">
              <input
                type="checkbox"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
                data-testid="upload-publish-checkbox"
              />
              Publish immediately (uncheck to save as draft)
            </label>

            <button
              type="submit"
              disabled={uploading}
              data-testid="upload-submit"
              className="inline-flex items-center gap-2 bg-ink text-white px-7 py-4 rounded-full text-sm font-semibold hover:bg-slate2 disabled:opacity-60"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Extracting…
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload & extract
                </>
              )}
            </button>
          </form>
        ) : loading ? (
          <p className="text-muted2 text-sm">Loading post…</p>
        ) : (
          <form onSubmit={handleSave} className="space-y-6" data-testid="edit-form">
            <p className="overline text-ink">Editing</p>
            <h1 className="font-display text-ink text-3xl tracking-tight">{title || "Untitled"}</h1>
            <p className="text-muted2 text-xs">{BLOG_PATH}/{slug}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Title">
                <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
              </Field>
              <Field label="Category">
                <Select value={category} onChange={(e) => setCategory(e.target.value)}>
                  {BLOG_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Field label="Excerpt (appears on the blog list and social preview)">
              <Textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={3} />
            </Field>

            <Field label="Tags (comma-separated)">
              <Input value={tags} onChange={(e) => setTags(e.target.value)} />
            </Field>

            <Field label="Featured image">
              {featuredImage && (
                <img
                  src={featuredImage}
                  alt="Featured"
                  className="mb-3 w-full max-w-md aspect-video object-cover rounded-md border border-border2"
                />
              )}
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 bg-white border border-border2 rounded-md px-4 py-2 text-sm cursor-pointer hover:border-ink">
                  <ImageIcon className="w-4 h-4" />
                  <span>{featuredImage ? "Replace" : "Choose image"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const r = new FileReader();
                      r.onload = () => setFeaturedImage(String(r.result));
                      r.readAsDataURL(f);
                    }}
                  />
                </label>
                {featuredImage && (
                  <button
                    type="button"
                    onClick={() => setFeaturedImage("")}
                    className="text-sm text-muted2 hover:text-red-600"
                  >
                    Remove
                  </button>
                )}
              </div>
            </Field>

            <Field
              label="Body (HTML — edit text, fix line breaks, paste image URLs as needed)"
              hint="The extracted PDF text lives here. You can clean, reorder, or add HTML."
            >
              <Textarea
                value={contentHtml}
                onChange={(e) => setContentHtml(e.target.value)}
                rows={20}
                data-testid="edit-content-html"
                className="font-mono text-xs"
              />
            </Field>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 bg-ink text-white px-7 py-3 rounded-full text-sm font-semibold hover:bg-slate2 disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Saving…" : "Save changes"}
              </button>
              {published && (
                <a
                  href={`${BLOG_PATH}/${slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-ink underline"
                >
                  View live →
                </a>
              )}
            </div>
          </form>
        )}
      </section>
    </main>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted2 mb-2 inline-block">
        {label}
        {required && <span className="text-brass ml-1">*</span>}
      </label>
      {hint && <p className="text-xs text-muted2 -mt-1 mb-2">{hint}</p>}
      {children}
    </div>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className={`w-full bg-white border border-border2 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-ink ${props.className || ""}`}
    />
  );
}

function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full bg-white border border-border2 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-ink resize-y ${props.className || ""}`}
    />
  );
}

function Select(props) {
  return (
    <select
      {...props}
      className={`w-full bg-white border border-border2 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-ink ${props.className || ""}`}
    >
      {props.children}
    </select>
  );
}
